import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { isStagesLockedError } from "@/features/stages/utils/lock.ts";
import { useApplyTemplate } from "../../hooks/use-apply-template.ts";
import { useStageTemplates } from "../../hooks/use-stage-templates.ts";

export function ApplyTemplateDialog({
	open,
	onOpenChange,
	constructionId,
	templatesPath,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	templatesPath: string;
}) {
	const templates = useStageTemplates();
	const applyTemplate = useApplyTemplate(constructionId);
	const [selectedId, setSelectedId] = useState("");
	const [formError, setFormError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setSelectedId("");
			setFormError(null);
		}
	}, [open]);

	async function handleApply() {
		if (!selectedId) {
			setFormError("Selecione um template para aplicar.");
			return;
		}
		setFormError(null);
		try {
			const result = await applyTemplate.mutateAsync(selectedId);
			const n = result.count;
			toast.success(
				`${n} ${n === 1 ? "etapa adicionada" : "etapas adicionadas"} a partir do template!`,
			);
			onOpenChange(false);
		} catch (err) {
			if (isStagesLockedError(err)) {
				onOpenChange(false);
				return;
			}
			setFormError(
				err instanceof APIError
					? err.message
					: err instanceof Error
						? err.message
						: "Houve um erro ao aplicar o template.",
			);
		}
	}

	const list = templates.data ?? [];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Aplicar template</DialogTitle>
					<DialogDescription>
						As etapas do template são adicionadas após as etapas já existentes
						neste canteiro.
					</DialogDescription>
				</DialogHeader>

				{templates.isPending ? (
					<div className="form-group">
						<Skeleton className="apply-template__skeleton-label" />
						<Skeleton className="apply-template__skeleton-select" />
					</div>
				) : templates.isError ? (
					<p className="apply-template__message">
						Não foi possível carregar os templates.
					</p>
				) : list.length === 0 ? (
					<div className="apply-template__empty">
						<p className="apply-template__empty-text">
							Nenhum template cadastrado ainda.
						</p>
						<Button asChild variant="outline">
							<Link to={templatesPath}>Criar um template</Link>
						</Button>
					</div>
				) : (
					<div className="form-group">
						<Label htmlFor="apply-template-select">Template</Label>
						<Select value={selectedId} onValueChange={setSelectedId}>
							<SelectTrigger
								id="apply-template-select"
								className="apply-template__select"
							>
								<SelectValue placeholder="Selecione um template" />
							</SelectTrigger>
							<SelectContent>
								{list.map((template) => (
									<SelectItem key={template.id} value={template.id}>
										{template.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{formError && <p className="form-error">{formError}</p>}
					</div>
				)}

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={applyTemplate.isPending}
					>
						Cancelar
					</Button>
					{list.length > 0 && (
						<Button
							type="button"
							onClick={handleApply}
							disabled={applyTemplate.isPending}
						>
							{applyTemplate.isPending && (
								<Loader2 className="template-spinner" />
							)}
							Aplicar
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
